"use client";

import { useTranslation } from "react-i18next";
import { Workflow } from "lucide-react";
import { ParsedMessageContent } from "@/components/ai-elements/parsed-message";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

export interface WorkflowTriggerMessage {
  prompt: string;
  requestedBy: string | null;
  note: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function textValue(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function workflowContextFromPrompt(prompt: string): {
  prompt: string;
  requestedBy: string | null;
  note: string | null;
} {
  // Dashboard workflow manifests often embed their inputs in this final line
  // rather than repeating them as top-level JSON fields. Parse the documented
  // shape so the card can surface those values without showing a raw envelope.
  const contextMatch = prompt.match(
    /(?:^|\r?\n)\s*Trigger context:\s*requested_by\s*=\s*([^;\r\n]+?)\s*;\s*note\s*=\s*([^\r\n]+?)\s*\.?\s*$/i,
  );
  if (!contextMatch) {
    return { prompt, requestedBy: null, note: null };
  }

  const contextStart = contextMatch.index ?? prompt.length;
  const displayPrompt = prompt.slice(0, contextStart).trimEnd() || prompt;
  return {
    prompt: displayPrompt,
    requestedBy: textValue(contextMatch[1]),
    note: textValue(contextMatch[2]),
  };
}

/**
 * Recognize the persisted envelope produced for a workflow agent step.
 *
 * This is intentionally UI-only: older servers need no new field or endpoint
 * to render existing workflow conversations. Invalid JSON or values outside
 * the narrow object-plus-string-prompt shape use the regular user bubble.
 */
export function parseWorkflowTriggerMessage(text: string): WorkflowTriggerMessage | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!isRecord(parsed)) return null;

    const prompt = textValue(parsed.prompt);
    if (!prompt) return null;

    const promptContext = workflowContextFromPrompt(prompt);
    const nestedContext = isRecord(parsed.trigger_context)
      ? parsed.trigger_context
      : isRecord(parsed.context)
        ? parsed.context
        : null;
    const requestedBy = textValue(parsed.requested_by)
      ?? textValue(parsed.requestedBy)
      ?? (nestedContext ? textValue(nestedContext.requested_by) : null)
      ?? promptContext.requestedBy;
    const note = textValue(parsed.note)
      ?? (nestedContext ? textValue(nestedContext.note) : null)
      ?? promptContext.note;

    return {
      prompt: promptContext.prompt,
      requestedBy,
      note,
    };
  } catch {
    return null;
  }
}

interface WorkflowMetadataBadgeProps {
  label: string;
  value: string;
}

function WorkflowMetadataBadge({ label, value }: WorkflowMetadataBadgeProps) {
  return (
    <Badge
      aria-label={`${label}: ${value}`}
      className="max-w-full gap-1 px-1.5 py-0 text-[10px] font-medium"
      title={`${label}: ${value}`}
      variant="secondary"
    >
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="truncate">{value}</span>
    </Badge>
  );
}

export interface WorkflowTriggerMessageCardProps {
  workflowMessage: WorkflowTriggerMessage;
  onFileLinkClick?: (url: string, fileName: string) => void;
}

/** A compact, human-readable renderer for dashboard workflow prompts. */
export function WorkflowTriggerMessageCard({
  workflowMessage,
  onFileLinkClick,
}: WorkflowTriggerMessageCardProps) {
  const { t } = useTranslation("chat");

  return (
    <Card asChild className="w-full p-3 text-left">
      <section
        aria-label={t("workflowTriggerMessage.cardLabel")}
        data-testid="workflow-trigger-message-card"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Workflow
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
          <span className="text-sm font-medium">
            {t("workflowTriggerMessage.title")}
          </span>
          {workflowMessage.requestedBy && (
            <WorkflowMetadataBadge
              label={t("workflowTriggerMessage.requestedBy")}
              value={workflowMessage.requestedBy}
            />
          )}
          {workflowMessage.note && (
            <WorkflowMetadataBadge
              label={t("workflowTriggerMessage.note")}
              value={workflowMessage.note}
            />
          )}
        </div>

        <Separator className="my-2.5" />

        <div className="min-w-0">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            {t("workflowTriggerMessage.prompt")}
          </p>
          {/* Workflow prompts are instruction text, so render their formatting. */}
          <ParsedMessageContent
            text={workflowMessage.prompt}
            onFileLinkClick={onFileLinkClick}
          />
        </div>
      </section>
    </Card>
  );
}
